import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import { Box, Grid2, Icon, IconProps, styled, Typography } from '@mui/material'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import React from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import CustomTooltip from '@/app/component/atom/tooltip'
import { formatExportFunction, formatExportStatus } from '@/app/module/exports/helpers'
import { Export, ExportStatus } from '@/app/module/exports/types'
import { filterOrgReportFiles, getOrgReportFile } from '@/app/module/reports/utils'
import { dateTime } from '@/app/service/util'
import { BackendFile } from '@/app/types'

import ExportActionMenu from './export-action-menu'

type Props = {
  index: number
  item: Export

  onDelete: (exportId: number) => Promise<boolean>
  onFileDownload: (exportId: number, filename: string) => Promise<boolean>
}

const ExportRow: React.FC<Props> = ({ index, item, onDelete, onFileDownload }) => {
  const navigate = useNavigate()

  const files = React.useMemo(() => filterOrgReportFiles(item.files || []), [item.files])
  const orgReportFile = React.useMemo(() => getOrgReportFile(item.files || []), [item.files])

  const handleOrgReportView = React.useCallback(
    (exportId: number) => {
      if (!orgReportFile) {
        return
      }

      navigate(`/reports/file/${exportId}?filename=${orgReportFile.filename}`)
    },
    [orgReportFile, navigate],
  )

  return (
    <Container className={`export-row export-${item.id}`} data-testid={`export-row-${index}`}>
      <Grid2 container>
        <Links size={{ xs: 5 }} padding={2}>
          {!files.length && (
            <span style={textStyles}>
              <Typography variant="subtitle2">
                <em>{noFilesMap[item.status]}</em>
              </Typography>
            </span>
          )}
          {files.map((file: BackendFile) => {
            if (item.status !== 'ready') {
              return (
                <span key={file.filename} style={textStyles}>
                  {file.filename}
                </span>
              )
            }

            return (
              <CustomTooltip key={file.filename} title="Click to download">
                <Link
                  href="#"
                  style={textStyles}
                  onClick={(e) => {
                    e.preventDefault()
                    onFileDownload(item.id, file.filename)
                  }}
                >
                  {file.filename} <FileDownloadOutlined />
                </Link>
              </CustomTooltip>
            )
          })}
        </Links>
        <Grid2 size={{ xs: 2 }} padding={2}>
          {formatExportFunction(item.function)}
        </Grid2>
        <Grid2 size={{ xs: 2 }} padding={2}>
          {dateTime(item.created)}
        </Grid2>
        <Grid2 size={{ xs: 2 }} padding={2}>
          <Box display="flex" alignItems="center">
            <Icon color={statusColorMap[item.status] || 'warning'}>circle</Icon>&nbsp;{formatExportStatus(item.status)}
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 1 }} padding={2}>
          <ExportActionMenu item={item} onDelete={onDelete} onView={orgReportFile ? handleOrgReportView : undefined} />
        </Grid2>
      </Grid2>
    </Container>
  )
}

const statusColorMap: Record<ExportStatus, IconProps['color']> = {
  queued: 'warning',
  inprogress: 'warning',
  ready: 'success',
  failed: 'error',
}

const noFilesMap: Record<ExportStatus, string> = {
  queued:
    'The export is in progress and no files are available yet. The files will be available once the export is complete.',
  inprogress:
    'The export is in progress and no files are available yet. The files will be available once the export is complete.',
  ready: 'No files available',
  failed: 'No files available',
}

const Container = styled(Paper)({
  height: '100%',
})

const Links = styled(Grid2)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const textStyles = {
  alignItems: 'flex-end',
  display: 'flex',
  fontSize: 16,
}

export default React.memo(ExportRow)
